// Pattern: docs/design-patterns/frontend/component-patterns.md (layout-structure-only)
// Pattern: docs/design-patterns/frontend/accessibility.md
// Pattern: docs/design-patterns/frontend/responsive-design.md
//
// Shared filter bar: a controlled search input, a flexible controls slot, and
// an optional "N of M" result count. Props in, JSX out — no data fetching, no
// store access (the caller owns searchValue/filtering state). Generalizes the
// search/controls/count idiom already used in PatternFilters.tsx.
import type { JSX, ReactNode } from 'react';
import { Input } from '@heroui/react';
import { Search } from 'lucide-react';

export interface FilterBarProps {
  /** Current (controlled) value of the search input. */
  searchValue: string;
  /** Invoked with the new value whenever the search input changes. */
  onSearchChange: (value: string) => void;
  /** Placeholder text; also used as the input's accessible name when set. */
  searchPlaceholder?: string;
  /** Opaque content rendered next to the search input (filters, sort, etc.). */
  controls?: ReactNode;
  /** Opaque content rendered at the trailing (right) end of the bar, immediately
   *  before the count — for page-level actions (e.g. a status select, a spec
   *  button) that belong on this row rather than wrapping with the page header. */
  actions?: ReactNode;
  /** When provided, renders a "{shown} of {total}" result count, right-aligned. */
  count?: { shown: number; total: number };
}

/**
 * Shared search + controls + count row. See `FilterBarProps` for the slot contract.
 */
export function FilterBar({
  actions,
  controls,
  count,
  onSearchChange,
  searchPlaceholder,
  searchValue,
}: FilterBarProps): JSX.Element {
  const accessibleSearchName = searchPlaceholder ?? 'Search';

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search + controls never wrap relative to each other (the input
          shrinks instead); the trailing actions/count group is the first
          thing to wrap onto its own row on narrow widths, per the FilterBar
          spec's responsive strategy ("count wraps below the search input
          and controls"). */}
      <div className="flex shrink-0 items-center gap-3">
        <Input
          aria-label={accessibleSearchName}
          placeholder={searchPlaceholder ?? 'Search…'}
          value={searchValue}
          onValueChange={onSearchChange}
          startContent={<Search className="h-4 w-4 text-muted" />}
          size="sm"
          className="w-48 min-w-0 sm:w-64"
          isClearable
          onClear={() => onSearchChange('')}
        />
        {controls && <div className="flex shrink-0 items-center gap-2">{controls}</div>}
      </div>
      {(actions || count) && (
        <div className="ml-auto flex shrink-0 items-center gap-3">
          {actions}
          {count && (
            <span className="text-sm text-muted shrink-0">
              {count.shown} of {count.total}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
